import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { fetchCaseMailThread } from "../../../api/case";
import { fetchCompanyAiMailboxMessage } from "../../../api/microsoft";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { CompanyAiMailboxMessageReaderDialog } from "../microsoft/CompanyAiMailboxMessageReaderDialog";
import { CaseEmailHtmlBody } from "./CaseEmailHtmlBody";
import {
  groupCaseMailThreads,
  mailBodyFromGraphMessage,
  sortMailThreadNewestFirst,
} from "../../../utils/mailThread";

export function CaseMailThreadDialog({
  open,
  onClose,
  caseId,
  mailboxId,
  mailboxEmail,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [threadMailbox, setThreadMailbox] = useState(null);
  const effectiveMailboxId = threadMailbox ? threadMailbox.mailbox_id : mailboxId;
  const effectiveMailboxEmail = threadMailbox ? threadMailbox.mailbox_email : mailboxEmail;
  const [loadedCaseId, setLoadedCaseId] = useState(null);
  const [error, setError] = useState(null);
  const [readerMsg, setReaderMsg] = useState(null);
  const [graphById, setGraphById] = useState({});

  useEffect(() => {
    if (!open || !caseId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setMessages([]);
      setThreadMailbox(null);
      setLoadedCaseId(null);
      setError(null);
      setGraphById({});
      setReaderMsg(null);
      const res = await fetchCaseMailThread(caseId);
      if (cancelled) return;
      if (res?.__error || res?.errors) {
        setMessages([]);
        setGraphById({});
        setError(res?.errors?.[0] || t("case.mail_thread.load_failed"));
        setLoading(false);
        return;
      }
      const list = Array.isArray(res?.data?.messages) ? res.data.messages : [];
      setMessages(list);
      setLoadedCaseId(caseId);
      setActiveConversation(res?.data?.mail_thread?.conversation_id || null);
      setThreadMailbox(res?.data?.mail_thread || null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, caseId, t]);

  const ordered = useMemo(() => sortMailThreadNewestFirst(messages), [messages]);
  const groups = useMemo(() => groupCaseMailThreads(messages), [messages]);
  const belongsToMailbox = (msg) => !msg.mailbox_email || Boolean(
    effectiveMailboxEmail && msg.mailbox_email.toLowerCase() === effectiveMailboxEmail.toLowerCase()
  );

  useEffect(() => {
    if (!open || loading || loadedCaseId !== caseId || !effectiveMailboxId) {
      setGraphById({});
      return undefined;
    }
    const ids = [
      ...new Set(ordered.filter((msg) => !msg.mailbox_email || (
        effectiveMailboxEmail && msg.mailbox_email.toLowerCase() === effectiveMailboxEmail.toLowerCase()
      )).map((msg) => msg?.graph_message_id).filter(Boolean)),
    ];
    if (ids.length === 0) {
      setGraphById({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        ids.map(async (gid) => {
          try {
            const data = await fetchCompanyAiMailboxMessage(effectiveMailboxId, gid);
            return [gid, data?.message || null];
          } catch {
            return [gid, null];
          }
        })
      );
      if (!cancelled) setGraphById(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loading, loadedCaseId, caseId, effectiveMailboxId, effectiveMailboxEmail, ordered]);

  const canOpenReader = Boolean(effectiveMailboxId && readerMsg?.graph_message_id);

  return (
    <>
      <PMDialog
        open={open}
        onClose={onClose}
        title="case.mail_thread.dialog_title"
        size="3xl"
        closeOnOutsideClick={!readerMsg}
        zIndex={2000}
      >
        {loading ? (
          <ResourceLoading />
        ) : error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : ordered.length === 0 ? (
          <p className="text-sm text-slate-500">{t("case.mail_thread.empty")}</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">{t("case.mail_thread.latest_first")}</p>
            {groups.map((group, index) => (
              <section key={group.key} aria-labelledby={`case-mail-thread-${index}`} className="space-y-3">
                <h3 id={`case-mail-thread-${index}`} className="border-b border-slate-200 pb-2 text-sm font-semibold text-slate-700">
                  {t("case.mail_thread.thread_number", { number: groups.length - index })}
                  {group.conversationId && group.conversationId === activeConversation && (!group.mailboxEmail || !effectiveMailboxEmail || group.mailboxEmail === effectiveMailboxEmail.toLowerCase()) ? (
                    <span className="ml-2 text-xs text-blue-700">{t("case.mail_thread.active_thread")}</span>
                  ) : null}
                </h3>
            {group.messages.map((msg) => {
              const inbound = msg.direction === "inbound";
              const graphPending = Boolean(
                effectiveMailboxId && belongsToMailbox(msg) && msg.graph_message_id && !(msg.graph_message_id in graphById)
              );
              const graph = belongsToMailbox(msg) && msg.graph_message_id
                ? graphById[msg.graph_message_id]
                : null;
              const fromGraph = mailBodyFromGraphMessage(graph);
              const html = fromGraph.html || msg.body_html;
              const text = fromGraph.html ? "" : fromGraph.text || msg.body_text;
              const canRead = Boolean(effectiveMailboxId && belongsToMailbox(msg) && msg.graph_message_id && graph);
              return (
                <article
                  key={msg.id}
                  className={`rounded-xl border px-4 py-3 ${
                    inbound ? "border-slate-200 bg-white" : "border-blue-100 bg-blue-50/40"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 text-xs font-semibold text-slate-600">
                    <span>
                      {inbound ? t("inbound") : t("outbound")}
                      {msg.from_email ? ` · ${msg.from_email}` : ""}
                    </span>
                    <span>
                      {msg.sent_at ? new Date(msg.sent_at).toLocaleString("nb-NO") : ""}
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {msg.subject || t("E-post")}
                  </div>
                  {graphPending && !html && !text ? (
                    <p className="mt-2 text-sm text-slate-500">
                      {t("case.mail_thread.body_loading")}
                    </p>
                  ) : (
                    <div className="mt-2">
                      <CaseEmailHtmlBody
                        html={html}
                        text={text}
                        title={
                          msg.subject || t("microsoft.ai_mailboxes.reader.body_iframe_title")
                        }
                      />
                    </div>
                  )}
                  {canRead ? (
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        onClick={() => setReaderMsg(msg)}
                      >
                        {t("case.mail_thread.open_message")}
                      </button>
                    </div>
                  ) : null}
                </article>
              );
            })}
              </section>
            ))}
          </div>
        )}
      </PMDialog>
      <CompanyAiMailboxMessageReaderDialog
        open={canOpenReader}
        onClose={() => setReaderMsg(null)}
        mailboxId={effectiveMailboxId}
        mailboxEmail={effectiveMailboxEmail}
        graphMessageId={readerMsg?.graph_message_id}
        previewSubject={readerMsg?.subject}
        zIndex={3600}
      />
    </>
  );
}
